import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface PulseLineProps {
  className?: string;
  color?: "primary" | "success" | "warning" | "danger" | "accent";
  animate?: boolean;
  duration?: number;
  height?: number;
  points?: number[];
}

export function PulseLine({
  className,
  color = "primary",
  animate = true,
  duration = 1.5,
  height = 40,
  points = [0.2, 0.5, 0.3, 0.8, 0.4, 0.9, 0.5, 0.7, 0.6, 0.85, 0.75, 0.65],
}: PulseLineProps) {
  const pathRef = useRef<SVGPathElement>(null);

  const colorMap = {
    primary: "hsl(var(--primary))",
    success: "hsl(var(--success))",
    warning: "hsl(var(--warning))",
    danger: "hsl(var(--danger))",
    accent: "hsl(var(--accent))",
  };

  const strokeColor = colorMap[color];

  // Generate SVG path from points
  const generatePath = () => {
    const width = 100;
    const padding = 4;
    const usableHeight = height - padding * 2;
    const stepX = width / (points.length - 1);

    let path = `M 0 ${height / 2}`;
    
    points.forEach((point, i) => {
      const x = i * stepX;
      const y = padding + usableHeight * (1 - point);
      
      if (i === 0) {
        path = `M ${x} ${y}`;
      } else {
        // Use smooth curves for a more organic feel
        const prevX = (i - 1) * stepX;
        const cpX = (prevX + x) / 2;
        path += ` Q ${cpX} ${y} ${x} ${y}`;
      }
    });

    return path;
  };

  useEffect(() => {
    if (animate && pathRef.current) {
      const path = pathRef.current;
      const length = path.getTotalLength();
      
      path.style.strokeDasharray = `${length}`;
      path.style.strokeDashoffset = `${length}`;
      
      // Trigger animation
      requestAnimationFrame(() => {
        path.style.transition = `stroke-dashoffset ${duration}s ease-out`;
        path.style.strokeDashoffset = "0";
      });
    }
  }, [animate, duration]);

  return (
    <svg
      viewBox={`0 0 100 ${height}`}
      className={cn("w-full", className)}
      preserveAspectRatio="none"
    >
      {/* Glow effect */}
      <defs>
        <filter id={`glow-${color}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      
      {/* Background line (subtle) */}
      <path
        d={generatePath()}
        fill="none"
        stroke={strokeColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.2"
      />
      
      {/* Animated foreground line */}
      <path
        ref={pathRef}
        d={generatePath()}
        fill="none"
        stroke={strokeColor}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter={`url(#glow-${color})`}
      />
      
      {/* End dot */}
      {animate && (
        <circle
          cx={100}
          cy={4 + (height - 8) * (1 - points[points.length - 1])}
          r="4"
          fill={strokeColor}
          className="animate-pulse"
          style={{ animationDelay: `${duration}s` }}
        />
      )}
    </svg>
  );
}

// Skeleton version for loading states
export function PulseLineSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("relative overflow-hidden", className)}>
      <svg viewBox="0 0 100 40" className="w-full h-10" preserveAspectRatio="none">
        <path
          d="M 0 20 Q 25 20 50 20 Q 75 20 100 20"
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-muted-foreground/10 to-transparent animate-pulse" />
    </div>
  );
}
